import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import PageLayout from '@/components/PageLayout';
import { useAuth } from '@/contexts/AuthProvider';
import { useI18n } from '@/i18n/LanguageContext';
import { useCar } from '@/contexts/CarContext';
import { useCarSelectionContext } from '@/contexts/CarSelectionContext';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
  AlertDialogAction,
} from '@/components/ui/alert-dialog';
import { ChevronRight, LogOut, Crown, MoreHorizontal, UserMinus, UserPlus, Share2, Clock, Copy, Check } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { getSignedViewUrl } from '@/lib/getSignedViewUrl';
import { toast } from '@/components/ui/use-toast';

const SharedAccessPage = () => {
  const { user } = useAuth();
  const { t } = useI18n();
  const { removeSharedUser, generateShareLink } = useCar();
  const { mainCarId } = useCarSelectionContext();
  const navigate = useNavigate();

  const [sharedCars, setSharedCars] = useState<Array<{
    id: string;
    brand: string;
    model: string;
    year?: number;
    nickname?: string;
    licensePlate?: string;
    imagePath?: string;
    isOwner: boolean;
    created_by: string;
  }>>([]);
  const [carImages, setCarImages] = useState<Record<string, string>>({});
  const [sharedUsers, setSharedUsers] = useState<Array<{
    id: string;
    email: string;
    displayName: string | null;
    avatarUrl: string | null;
    cars: Array<{
      id: string;
      brand: string;
      model: string;
      year?: number;
      nickname?: string;
      isOwner: boolean;
    }>;
  }>>([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState<'cars' | 'users'>('cars');
  const [manageAccessDrawerOpen, setManageAccessDrawerOpen] = useState(false);
  const [selectedCarId, setSelectedCarId] = useState<string | null>(null);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [userToRemove, setUserToRemove] = useState<string | null>(null);
  const [carToRemoveFrom, setCarToRemoveFrom] = useState<string | null>(null);
  const [removeUserDialogOpen, setRemoveUserDialogOpen] = useState(false);
  const [leaveCarDialogOpen, setLeaveCarDialogOpen] = useState(false);
  const [shareDrawerOpen, setShareDrawerOpen] = useState(false);
  const [selectedCarForShare, setSelectedCarForShare] = useState<string | null>(null);
  const [currentShareLink, setCurrentShareLink] = useState<string | null>(null);
  const [generatingLink, setGeneratingLink] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);
  const [activeInvitationsForShare, setActiveInvitationsForShare] = useState<Array<{
    id: string;
    token: string;
    expiresAt: string;
    createdAt: string;
  }>>([]);

  // Sort cars so main car is first
  const sortedSharedCars = useMemo(() => {
    if (!mainCarId) return sharedCars;
    return [...sharedCars].sort((a, b) => {
      if (a.id === mainCarId) return -1;
      if (b.id === mainCarId) return 1;
      return 0;
    });
  }, [sharedCars, mainCarId]);

  useEffect(() => {
    loadSharedAccess();
  }, [user?.id]);

  const loadSharedAccess = async () => {
    setLoading(true);
    
    try {
      if (!user?.id) {
        setLoading(false);
        return;
      }
      
      // Get all cars the user has access to via user_cars
      const { data: userCarsData, error: userCarsError } = await supabase
        .from('user_cars')
        .select('car_id')
        .eq('user_id', user.id);

      if (userCarsError) {
        console.error('Error loading shared cars:', userCarsError);
        setLoading(false);
        return;
      }

      // Also get cars where user is the creator (for backward compatibility)
      const { data: createdCarsData, error: createdCarsError } = await supabase
        .from('cars')
        .select('id')
        .eq('created_by', user.id)
        .is('deleted_at', null);

      if (createdCarsError) {
        console.error('Error loading created cars:', createdCarsError);
      }

      // Combine car IDs from both sources
      const carIdsFromUserCars = (userCarsData || []).map(uc => uc.car_id);
      const carIdsFromCreated = (createdCarsData || []).map(c => c.id);
      const allCarIds = Array.from(new Set([...carIdsFromUserCars, ...carIdsFromCreated]));

      if (allCarIds.length === 0) {
        console.log('No cars found for user');
        setSharedCars([]);
        setSharedUsers([]);
        setLoading(false);
        return;
      }

      // Get car details
      const { data: carsData, error: carsError } = await supabase
        .from('cars')
        .select('id, make, model, year, nickname, created_by, license_plate, image_path')
        .in('id', allCarIds)
        .is('deleted_at', null);

      if (carsError) {
        console.error('Error loading car details:', carsError);
        setLoading(false);
        return;
      }

      if (!carsData || carsData.length === 0) {
        console.log('No car details found');
        setSharedCars([]);
        setSharedUsers([]);
        setLoading(false);
        return;
      }

      console.log('Loaded cars:', carsData.length);

      // Map to shared cars format (map make to brand for consistency with frontend)
      const sharedCarsList = (carsData || []).map(car => ({
        id: car.id,
        brand: car.make || '', // Map make to brand
        model: car.model || '',
        year: car.year || undefined,
        nickname: car.nickname || undefined,
        licensePlate: car.license_plate || undefined,
        imagePath: car.image_path || undefined,
        isOwner: car.created_by === user.id,
        created_by: car.created_by
      }));

      setSharedCars(sharedCarsList);

      // Load car images
      const imageUrlMap: Record<string, string> = {};
      for (const car of sharedCarsList) {
        if (car.imagePath) {
          try {
            const signedUrlInfo = await getSignedViewUrl(car.imagePath);
            imageUrlMap[car.id] = signedUrlInfo.url;
          } catch (error) {
            console.error(`Failed to load car image for car ${car.id}:`, error);
          }
        }
      }
      setCarImages(imageUrlMap);

      // Now fetch users for "By Users" view
      // Get all user_cars entries for these cars
      const { data: allUserCarsData, error: allUserCarsError } = await supabase
        .from('user_cars')
        .select('user_id, car_id')
        .in('car_id', allCarIds);

      if (allUserCarsError) {
        console.error('Error loading all user cars:', allUserCarsError);
        setLoading(false);
        return;
      }

      // Get unique user IDs from user_cars
      const userIdsFromUserCars = Array.from(new Set((allUserCarsData || []).map(uc => uc.user_id)));
      
      // Also include original owners (created_by) even if not in user_cars
      const ownerIds = Array.from(new Set(sharedCarsList.map(car => car.created_by).filter(Boolean)));
      
      // Combine all user IDs and ensure current user is included
      const userIds = Array.from(new Set([...userIdsFromUserCars, ...ownerIds, user.id]));
      
      console.log('User IDs to fetch:', userIds.length);
      console.log('Car IDs:', allCarIds.length);
      console.log('Shared cars list:', sharedCarsList.length);
      
      // Fetch user details
      const { data: usersData, error: usersError } = await supabase
        .from('users')
        .select('id, email, custom_avatar_url')
        .in('id', userIds);

      if (usersError) {
        console.error('Error loading users:', usersError);
        // Don't return early - we can still show cars even without user data
      }

      // Ensure current user is in the list even if not found in database
      let finalUsersData = usersData || [];
      if (user && !finalUsersData.find(u => u.id === user.id)) {
        console.log('Adding current user to list');
        finalUsersData = [
          ...finalUsersData,
          {
            id: user.id,
            email: user.email || '',
            custom_avatar_url: null
          }
        ];
      }
      
      if (finalUsersData.length === 0) {
        console.log('No users found, but showing cars');
        setSharedUsers([]);
        setLoading(false);
        return;
      }

      // Get display names via Edge Function
      const displayNameMap = new Map<string, string | null>();
      
      // For current user, get display name from auth metadata
      if (user) {
        const meta: any = user?.user_metadata || {};
        const displayName = meta.display_name || meta.full_name || meta.name || 
          (meta.given_name && meta.family_name ? `${meta.given_name} ${meta.family_name}` : null);
        if (displayName) {
          displayNameMap.set(user.id, displayName);
        }
      }

      // Fetch display names for other users via Edge Function
      const otherUserIds = finalUsersData.filter(u => u.id !== user?.id).map(u => u.id);
      
      if (otherUserIds.length > 0) {
        try {
          const { data: { session } } = await supabase.auth.getSession();
          if (session) {
            const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || '';
            const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY || '';
            
            const response = await fetch(
              `${supabaseUrl}/functions/v1/get-user-display-names`,
              {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  'Authorization': `Bearer ${session.access_token}`,
                  'apikey': supabaseAnonKey,
                },
                body: JSON.stringify({ userIds: otherUserIds }),
              }
            );

            if (response.ok) {
              const data = await response.json();
              if (data.success && data.displayNames) {
                Object.entries(data.displayNames).forEach(([userId, displayName]) => {
                  displayNameMap.set(userId, displayName as string);
                });
              }
            }
          }
        } catch (error) {
          console.error('Error fetching display names:', error);
        }
      }

      // Load avatar URLs
      const avatarUrlMap: Record<string, string> = {};
      for (const u of finalUsersData) {
        let avatarUrl: string | null = null;
        
        // Check for custom avatar
        if (u.custom_avatar_url) {
          try {
            const signedUrlInfo = await getSignedViewUrl(u.custom_avatar_url);
            avatarUrl = signedUrlInfo.url;
          } catch (error) {
            console.error(`Failed to load custom avatar for user ${u.id}:`, error);
          }
        }
        
        // For current user, check auth metadata
        if (!avatarUrl && u.id === user?.id) {
          const meta: any = user?.user_metadata || {};
          avatarUrl = meta.avatar_url || meta.picture || null;
        }
        
        if (avatarUrl) {
          avatarUrlMap[u.id] = avatarUrl;
        }
      }

      // Build users with their cars
      const usersWithCars = finalUsersData.map(u => {
        // Get cars from user_cars
        const userCarIdsFromUserCars = (allUserCarsData || [])
          .filter(uc => uc.user_id === u.id)
          .map(uc => uc.car_id);
        
        // Also include cars where user is the original owner
        const ownedCarIds = sharedCarsList
          .filter(car => car.created_by === u.id)
          .map(car => car.id);
        
        // Combine both sources
        const allUserCarIds = Array.from(new Set([...userCarIdsFromUserCars, ...ownedCarIds]));
        
        const userCars = sharedCarsList.filter(car => allUserCarIds.includes(car.id));
        
        let displayName = displayNameMap.get(u.id);
        if (!displayName && u.email) {
          displayName = u.email.split('@')[0];
        }

        return {
          id: u.id,
          email: u.email || '',
          displayName: displayName || null,
          avatarUrl: avatarUrlMap[u.id] || null,
          cars: userCars.map(car => ({
            ...car,
            // Ensure created_by is included for Original Owner check
            created_by: car.created_by || ''
          }))
        };
      });

      // Ensure current user is always in the list with all their cars
      const currentUserInList = usersWithCars.find(u => u.id === user.id);
      if (!currentUserInList && user) {
        // Add current user if missing
        const meta: any = user?.user_metadata || {};
        const displayName = meta.display_name || meta.full_name || meta.name || 
          (meta.given_name && meta.family_name ? `${meta.given_name} ${meta.family_name}` : null) ||
          user.email?.split('@')[0] || null;
        
        const avatarUrl = meta.avatar_url || meta.picture || null;
        
        // Get all cars where current user is owner or has access
        const currentUserCars = sharedCarsList.filter(car => 
          car.created_by === user.id || 
          (allUserCarsData || []).some(uc => uc.user_id === user.id && uc.car_id === car.id)
        ).map(car => ({
          ...car,
          // Ensure created_by is included for Original Owner check
          created_by: car.created_by || ''
        }));
        
        usersWithCars.push({
          id: user.id,
          email: user.email || '',
          displayName: displayName,
          avatarUrl: avatarUrl,
          cars: currentUserCars
        });
      }

      console.log('Built users with cars:', usersWithCars.length);
      console.log('Users with cars details:', usersWithCars.map(u => ({
        id: u.id,
        email: u.email,
        displayName: u.displayName,
        carsCount: u.cars.length,
        carIds: u.cars.map(c => c.id)
      })));
      setSharedUsers(usersWithCars);
    } catch (error) {
      console.error('Error loading shared access:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLeaveSharedCar = async (carId: string) => {
    if (!user?.id) return;
    
    try {
      await removeSharedUser(carId, user.id);
      toast({
        title: t('carSharing.leaveCar'),
        description: t('carSharing.leftCarSuccessShort'),
      });
      setLeaveCarDialogOpen(false);
      setManageAccessDrawerOpen(false);
      // Reload shared cars list
      await loadSharedAccess();
    } catch (error: any) {
      console.error('Error leaving car:', error);
      toast({
        title: t('carSharing.leaveCar'),
        description: error.message || t('carSharing.failedToLeaveCar'),
        variant: 'destructive',
      });
    }
  };

  const handleOpenManageAccessForCar = (carId: string) => {
    setSelectedCarId(carId);
    setSelectedUserId(null);
    setManageAccessDrawerOpen(true);
  };

  const handleOpenManageAccessForUser = (userId: string) => {
    setSelectedUserId(userId);
    setSelectedCarId(null);
    setManageAccessDrawerOpen(true);
  };

  const handleRemoveUser = async () => {
    const carId = carToRemoveFrom || selectedCarId;
    if (!carId || !userToRemove) return;
    
    try {
      await removeSharedUser(carId, userToRemove);
      toast({
        title: t('carSharing.removeUser'),
        description: t('carSharing.userRemovedSuccess'),
      });
      setRemoveUserDialogOpen(false);
      setUserToRemove(null);
      setCarToRemoveFrom(null);
      // Reload shared cars list
      await loadSharedAccess();
    } catch (error: any) {
      console.error('Error removing user:', error);
      toast({
        title: t('carSharing.removeUser'),
        description: error.message || t('carSharing.failedToRemoveUser'),
        variant: 'destructive',
      });
    }
  };

  const handleRemoveUserFromCar = (carId: string, userId: string) => {
    if (!user?.id) return;
    
    // Check if current user is the owner of this car
    const car = sortedSharedCars.find(c => c.id === carId);
    if (!car || car.created_by !== user.id) {
      toast({
        title: t('carSharing.removeUser'),
        description: t('carSharing.onlyOwnerCanRemove'),
        variant: 'destructive',
      });
      return;
    }
    
    // Set the user and car to remove, then open the dialog
    setUserToRemove(userId);
    setCarToRemoveFrom(carId);
    setRemoveUserDialogOpen(true);
  };

  const formatTimeRemaining = (expiresAt: string): string => {
    const now = new Date();
    const expires = new Date(expiresAt);
    const diff = expires.getTime() - now.getTime();

    if (diff <= 0) return t('carSharing.expired');

    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

    if (hours > 0) {
      return `${hours}h ${minutes}m ${t('carSharing.remaining')}`;
    }
    return `${minutes}m ${t('carSharing.remaining')}`;
  };

  // Load active invitations when share drawer opens
  useEffect(() => {
    const loadActiveInvitations = async () => {
      if (!selectedCarForShare || !user) return;
      
      try {
        const now = new Date().toISOString();
        const { data: invitationsData, error: invitationsError } = await supabase
          .from('car_share_invitations')
          .select('id, token, expires_at, created_at, claimed_by, claimed_at')
          .eq('car_id', selectedCarForShare)
          .eq('created_by', user.id)
          .is('claimed_by', null)
          .is('claimed_at', null)
          .gt('expires_at', now)
          .order('created_at', { ascending: false });

        if (invitationsError) {
          console.error('Error loading active invitations:', invitationsError);
          setActiveInvitationsForShare([]);
          return;
        }

        setActiveInvitationsForShare((invitationsData || []).map(inv => ({
          id: inv.id,
          token: inv.token,
          expiresAt: inv.expires_at,
          createdAt: inv.created_at
        })));

        // Set current share link if there's an active invitation
        if (invitationsData && invitationsData.length > 0) {
          const origin = window.location.origin;
          setCurrentShareLink(`${origin}/share/car/${invitationsData[0].token}`);
        } else {
          setCurrentShareLink(null);
        }
      } catch (error) {
        console.error('Error loading active invitations:', error);
        setActiveInvitationsForShare([]);
      }
    };

    if (shareDrawerOpen && selectedCarForShare) {
      loadActiveInvitations();
    }
  }, [shareDrawerOpen, selectedCarForShare, user?.id]);

  const handleGenerateLink = async () => {
    if (!selectedCarForShare) return;
    
    setGeneratingLink(true);
    try {
      const result = await generateShareLink(selectedCarForShare);
      setCurrentShareLink(result.shareLink);
      
      // Refresh active invitations
      const now = new Date().toISOString();
      const { data: invitationsData } = await supabase
        .from('car_share_invitations')
        .select('id, token, expires_at, created_at, claimed_by, claimed_at')
        .eq('car_id', selectedCarForShare)
        .eq('created_by', user?.id)
        .is('claimed_by', null)
        .is('claimed_at', null)
        .gt('expires_at', now)
        .order('created_at', { ascending: false });
      
      if (invitationsData && invitationsData.length > 0) {
        setActiveInvitationsForShare(invitationsData.map(inv => ({
          id: inv.id,
          token: inv.token,
          expiresAt: inv.expires_at,
          createdAt: inv.created_at
        })));
      }
    } catch (error: any) {
      console.error('Error generating link:', error);
      toast({
        title: t('carSharing.generateLink'),
        description: error.message || t('carSharing.failedToGenerateLink'),
        variant: 'destructive',
      });
    } finally {
      setGeneratingLink(false);
    }
  };

  const handleCopyLinkFromDrawer = async () => {
    if (currentShareLink) {
      try {
        await navigator.clipboard.writeText(currentShareLink);
        setLinkCopied(true);
        toast({
          title: t('carSharing.linkCopied'),
          description: t('carSharing.shareLinkCopied'),
        });
        setTimeout(() => {
          setLinkCopied(false);
        }, 2000);
      } catch (error) {
        console.error('Failed to copy link:', error);
        toast({
          title: t('carSharing.copyError'),
          description: t('carSharing.copyErrorDescription'),
          variant: 'destructive',
        });
      }
    }
  };

  const handleShareLink = async () => {
    if (!currentShareLink) return;
    
    if (navigator.share) {
      try {
        await navigator.share({
          title: t('carSharing.shareLink'),
          text: t('carSharing.shareLinkMessage'),
          url: currentShareLink,
        });
      } catch (error: any) {
        if (error.name !== 'AbortError') {
          console.error('Error sharing:', error);
        }
      }
    } else {
      handleCopyLinkFromDrawer();
    }
  };

  const handleDeactivateCurrentLink = async () => {
    if (activeInvitationsForShare.length > 0 && selectedCarForShare) {
      try {
        const { error } = await supabase
          .from('car_share_invitations')
          .delete()
          .eq('id', activeInvitationsForShare[0].id)
          .eq('created_by', user?.id);

        if (error) throw error;

        setActiveInvitationsForShare([]);
        setCurrentShareLink(null);
        
        toast({
          title: t('carSharing.inviteDeactivated'),
          description: t('carSharing.inviteDeactivatedDescription'),
        });
      } catch (error: any) {
        console.error('Error deactivating invite:', error);
        toast({
          title: t('carSharing.error'),
          description: error.message || t('carSharing.failedToDeactivateInvite'),
          variant: 'destructive',
        });
      }
    }
  };

  const handleRemoveUserFromAllCars = async () => {
    if (!selectedUserId || !user?.id) return;
    
    // Get all cars owned by current user
    const ownedCars = sortedSharedCars.filter(car => car.created_by === user.id);
    
    if (ownedCars.length === 0) {
      toast({
        title: t('carSharing.removeUser'),
        description: t('carSharing.youDoNotOwnAnyCars'),
        variant: 'destructive',
      });
      return;
    }

    try {
      // Remove user from all owned cars
      await Promise.all(ownedCars.map(car => removeSharedUser(car.id, selectedUserId)));
      toast({
        title: t('carSharing.removeUser'),
        description: t('carSharing.userRemovedFromCarsCount').replace('{count}', String(ownedCars.length)),
      });
      // Reload shared cars list
      await loadSharedAccess();
    } catch (error: any) {
      console.error('Error removing user from all cars:', error);
      toast({
        title: t('carSharing.removeUser'),
        description: error.message || t('carSharing.failedToRemoveFromAllCars'),
        variant: 'destructive',
      });
    }
  };

  return (
    <PageLayout 
      title={t('profile.sharedAccess')} 
      showBackButton 
      onBack={() => navigate(-1)}
    >
      <div className="space-y-6 pb-16 px-2 sm:px-4">
        {/* Toggle Switch */}
        <div className="flex items-center justify-center mt-4 mb-4">
          <Tabs value={viewMode} onValueChange={(v) => setViewMode(v as 'cars' | 'users')} className="w-full max-w-md">
            <TabsList className="w-full grid grid-cols-2 p-0 m-0 rounded-lg bg-gray-100 gap-1">
              <TabsTrigger value="cars" className="w-full h-9 rounded-lg data-[state=active]:bg-white data-[state=active]:shadow flex items-center justify-center" style={{ fontFamily: 'Lato' }}>
                {t('profile.cars')}
              </TabsTrigger>
              <TabsTrigger value="users" className="w-full h-9 rounded-lg data-[state=active]:bg-white data-[state=active]:shadow flex items-center justify-center" style={{ fontFamily: 'Lato' }}>
                {t('profile.users')}
              </TabsTrigger>
            </TabsList>
          </Tabs>
        </div>

        <div className="space-y-4">
          {loading ? (
            <div className="text-center py-12 text-gray-500 text-sm" style={{ fontFamily: 'Lato' }}>
              {t('carSharing.loading')}
            </div>
          ) : viewMode === 'cars' ? (
            // By Cars View
            sortedSharedCars.length === 0 ? (
              <div className="text-center py-12 text-gray-500 text-sm" style={{ fontFamily: 'Lato' }}>
                {t('carSharing.noSharedCars')}
              </div>
            ) : (
              sortedSharedCars.map((car) => {
                // Get users who have access to this car
                const carUsers = sharedUsers.filter(u => u.cars.some(c => c.id === car.id));
                console.log(`Car ${car.id} (${car.nickname || car.brand}): ${carUsers.length} users`, carUsers.map(u => u.email));
                
                return (
                  <div
                    key={car.id}
                    className="border rounded-2xl p-5 bg-[#FDFDFD]"
                    style={{ boxShadow: '0px 10px 8px -1px #EEEEEE', borderColor: '#e4e4e7' }}
                  >
                    {/* Car Header */}
                    <div className="flex items-center gap-4 mb-4">
                      {/* Car Image - Clickable */}
                      <button
                        onClick={() => navigate(`/cars/${car.id}`)}
                        className="h-20 w-20 rounded-lg overflow-hidden flex-shrink-0 bg-gray-100 cursor-pointer hover:opacity-90 transition-opacity"
                      >
                        {carImages[car.id] ? (
                          <img 
                            src={carImages[car.id]} 
                            alt={car.nickname || `${car.brand} ${car.model}`}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <img
                              src="/icons/AddCarImage.png"
                              alt={t('common.addCarImage')}
                              className="w-8 h-8 object-contain opacity-50"
                            />
                          </div>
                        )}
                      </button>
                      
                      {/* Car Details */}
                      <div className="flex-1 min-w-0">
                        <div className="flex flex-col items-end gap-1">
                          {car.nickname && (
                            <span className="font-medium text-gray-900 text-base" style={{ fontFamily: 'Lato' }}>
                              {car.nickname}
                            </span>
                          )}
                          {(car.brand || car.model) && (
                            <div className="text-sm text-gray-600" style={{ fontFamily: 'Lato' }}>
                              {[car.brand, car.model].filter(Boolean).join(' ')}
                            </div>
                          )}
                          {car.year && (
                            <span className="text-sm text-gray-600" style={{ fontFamily: 'Lato' }}>
                              {car.year}
                            </span>
                          )}
                          {car.licensePlate && (
                            <span className="text-sm text-gray-500" style={{ fontFamily: 'Lato' }}>
                              {car.licensePlate}
                            </span>
                          )}
                        </div>
                      </div>
                      
                      {!car.isOwner && (
                        <div className="flex items-center gap-2 flex-shrink-0">
                          <Button
                            variant="secondary"
                            size="sm"
                            onClick={() => handleLeaveSharedCar(car.id)}
                            className="flex items-center gap-1 text-xs"
                            style={{ fontFamily: 'Lato' }}
                          >
                            <LogOut className="h-3 w-3" />
                            {t('carSharing.leaveCar')}
                          </Button>
                        </div>
                      )}
                    </div>
                    
                    {/* Users with access - Always show this section */}
                    <div className="mt-4 pt-4 border-t" style={{ borderColor: '#e4e4e7' }}>
                      <div className="flex items-center justify-between mb-3">
                        <p className="text-sm text-gray-500 font-medium" style={{ fontFamily: 'Lato' }}>
                          {t('profile.usersWithAccess')} ({carUsers.length})
                        </p>
                        {car.isOwner && (
                          <button
                            onClick={() => {
                              setSelectedCarForShare(car.id);
                              setShareDrawerOpen(true);
                            }}
                            className="flex items-center justify-center bg-[#E7FE52] text-black rounded-[10px] h-9 w-9 shadow-sm text-sm font-medium active:opacity-80"
                            style={{ boxShadow: '0px 1px 2px rgba(10,12.67,18,0.05)' }}
                            aria-label={t('carSharing.addAnotherUserAria')}
                          >
                            <UserPlus className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                      {carUsers.length > 0 ? (
                        <div className="space-y-3">
                          {carUsers.map((u) => {
                            const isOriginalOwner = car.created_by === u.id;
                            return (
                              <div key={u.id} className="flex items-center gap-3">
                                {/* Avatar */}
                                {u.avatarUrl ? (
                                  <div className="h-10 w-10 rounded-full overflow-hidden flex-shrink-0">
                                    <img 
                                      src={u.avatarUrl} 
                                      alt={u.displayName || u.email}
                                      className="w-full h-full object-cover"
                                    />
                                  </div>
                                ) : (
                                  <div className="h-10 w-10 rounded-full bg-[#7c9ffa] text-white flex items-center justify-center text-sm font-semibold flex-shrink-0">
                                    {u.displayName?.[0]?.toUpperCase() || u.email[0]?.toUpperCase() || 'U'}
                                  </div>
                                )}
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm text-gray-900 font-medium truncate" style={{ fontFamily: 'Lato' }}>
                                    {u.displayName || u.email}
                                  </p>
                                  <div className="flex items-center gap-2 mt-1 flex-wrap">
                                    {isOriginalOwner && (
                                      <span className="px-1.5 py-0.5 text-[10px] border border-gray-900 rounded-full flex items-center gap-0.5 flex-shrink-0" style={{ fontFamily: 'Lato' }}>
                                        <Crown className="h-2.5 w-2.5" />
                                        {t('carSharing.originalOwner')}
                                      </span>
                                    )}
                                    {u.id === user?.id && (
                                      <span className="px-1.5 py-0.5 text-[10px] border border-gray-900 rounded-full flex-shrink-0" style={{ fontFamily: 'Lato' }}>
                                        {t('carSharing.you')}
                                      </span>
                                    )}
                                  </div>
                                </div>
                                <button
                                  onClick={() => handleOpenManageAccessForCar(car.id)}
                                  className="p-1 hover:bg-gray-100 rounded-full transition-colors flex-shrink-0"
                                  aria-label={t('carSharing.manageAccessAria')}
                                >
                                  <MoreHorizontal className="h-5 w-5 text-gray-600" />
                                </button>
                              </div>
                            );
                          })}
                        </div>
                      ) : (
                        <p className="text-sm text-gray-500" style={{ fontFamily: 'Lato' }}>
                          {t('carSharing.noUsers')}
                        </p>
                      )}
                    </div>
                  </div>
                );
              })
            )
          ) : (
            // By Users View
            sharedUsers.length === 0 ? (
              <div className="text-center py-12 text-gray-500 text-sm" style={{ fontFamily: 'Lato' }}>
                {t('carSharing.noUsersFound')}
              </div>
            ) : (
              sharedUsers.map((u) => (
                <div
                  key={u.id}
                  className="border rounded-2xl p-5 bg-[#FDFDFD]"
                  style={{ boxShadow: '0px 10px 8px -1px #EEEEEE', borderColor: '#e4e4e7' }}
                >
                  {/* User Header */}
                  <div className="flex items-center gap-4 mb-4">
                    {/* Avatar */}
                    {u.avatarUrl ? (
                      <div className="h-12 w-12 rounded-full overflow-hidden flex-shrink-0">
                        <img 
                          src={u.avatarUrl} 
                          alt={u.displayName || u.email}
                          className="w-full h-full object-cover"
                        />
                      </div>
                    ) : (
                      <div className="h-12 w-12 rounded-full bg-[#7c9ffa] text-white flex items-center justify-center text-lg font-semibold flex-shrink-0">
                        {u.displayName?.[0]?.toUpperCase() || u.email[0]?.toUpperCase() || 'U'}
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-gray-900 text-base" style={{ fontFamily: 'Lato' }}>
                        {u.displayName || u.email}
                      </p>
                      {u.id === user?.id && (
                        <span className="px-2 py-0.5 text-xs border border-gray-900 rounded-full inline-block mt-1" style={{ fontFamily: 'Lato' }}>
                          {t('carSharing.you')}
                        </span>
                      )}
                    </div>
                    <button
                      onClick={() => handleOpenManageAccessForUser(u.id)}
                      className="p-1 hover:bg-gray-100 rounded-full transition-colors flex-shrink-0"
                      aria-label={t('carSharing.manageAccessAria')}
                    >
                      <MoreHorizontal className="h-5 w-5 text-gray-600" />
                    </button>
                  </div>
                  
                  {/* Cars with access */}
                  {u.cars.length > 0 && (
                    <div className="mt-4 pt-4 border-t" style={{ borderColor: '#e4e4e7' }}>
                      <p className="text-sm text-gray-500 mb-3 font-medium" style={{ fontFamily: 'Lato' }}>
                        {t('profile.carsWithAccess')} ({u.cars.length})
                      </p>
                      <div className="space-y-2">
                        {u.cars.map((car) => {
                          // Check if this user is the original owner of this car
                          const isOriginalOwner = (car as any).created_by === u.id;
                          return (
                            <div 
                              key={car.id} 
                              className="flex items-center justify-between p-3 rounded-lg hover:bg-gray-50 cursor-pointer border border-gray-100"
                              onClick={() => navigate(`/cars/${car.id}`)}
                            >
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <span className="text-sm text-gray-900 font-medium truncate" style={{ fontFamily: 'Lato' }}>
                                    {car.nickname || `${car.brand} ${car.model}`}
                                  </span>
                                  {isOriginalOwner && (
                                    <span className="px-1.5 py-0.5 text-[10px] border border-gray-900 rounded-full flex items-center gap-0.5" style={{ fontFamily: 'Lato' }}>
                                      <Crown className="h-2.5 w-2.5" />
                                      {t('carSharing.originalOwner')}
                                    </span>
                                  )}
                                </div>
                                <p className="text-xs text-gray-500 mt-1" style={{ fontFamily: 'Lato' }}>
                                  {[car.brand, car.model].filter(Boolean).join(' ')}
                                  {(car as any).licensePlate && ` | ${(car as any).licensePlate}`}
                                </p>
                              </div>
                              <ChevronRight className="w-5 h-5 text-gray-400 flex-shrink-0" />
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              ))
            )
          )}
        </div>
      </div>

      {/* Manage Access Bottom Drawer */}
      <Sheet open={manageAccessDrawerOpen} onOpenChange={setManageAccessDrawerOpen}>
        <SheetContent side="bottom" className="h-auto max-h-[90vh] rounded-t-2xl p-6 border-t border-gray-200">
          <SheetHeader>
            {selectedUserId && (() => {
              const selectedUser = sharedUsers.find(u => u.id === selectedUserId);
              return selectedUser ? (
                <div className="flex flex-col items-center mb-2">
                  {selectedUser.avatarUrl ? (
                    <div className="h-20 w-20 rounded-full overflow-hidden flex-shrink-0 mb-2">
                      <img 
                        src={selectedUser.avatarUrl} 
                        alt={selectedUser.displayName || selectedUser.email}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  ) : (
                    <div className="h-20 w-20 rounded-full bg-[#7c9ffa] text-white flex items-center justify-center text-2xl font-semibold flex-shrink-0 mb-2">
                      {selectedUser.displayName?.[0]?.toUpperCase() || selectedUser.email[0]?.toUpperCase() || 'U'}
                    </div>
                  )}
                </div>
              ) : null;
            })()}
            <SheetTitle className="text-lg font-semibold text-black text-center" style={{ fontFamily: 'Lato', fontWeight: 700, fontSize: '18px', lineHeight: '24px' }}>
              {selectedUserId 
                ? (() => {
                    const selectedUser = sharedUsers.find(u => u.id === selectedUserId);
                    return selectedUser?.displayName || selectedUser?.email || t('carSharing.manageAccess');
                  })()
                : t('carSharing.manageAccess')}
            </SheetTitle>
            <p className="text-sm text-gray-500 text-center mt-1" style={{ fontFamily: 'Lato' }}>
              {selectedCarId 
                ? (() => {
                    const car = sortedSharedCars.find(c => c.id === selectedCarId);
                    const carName = car?.nickname || car?.brand || t('carSharing.thisCar');
                    return t('carSharing.manageAccessDescriptionWithCar').replace('{carName}', carName);
                  })()
                : selectedUserId
                ? (() => {
                    const selectedUser = sharedUsers.find(u => u.id === selectedUserId);
                    const userName = selectedUser?.displayName || selectedUser?.email || t('carSharing.thisUser');
                    return t('carSharing.manageCarsForUser').replace('{userName}', userName);
                  })()
                : t('carSharing.manageAccess')}
            </p>
          </SheetHeader>
          
          <div className="mt-6 space-y-4">
            {selectedCarId ? (
              // Show users for selected car
              (() => {
                const car = sortedSharedCars.find(c => c.id === selectedCarId);
                const carUsers = sharedUsers.filter(u => u.cars.some(c => c.id === selectedCarId));
                const isOriginalOwner = car?.created_by === user?.id;
                
                return carUsers.length === 0 ? (
                  <div className="text-center py-8 text-gray-500 text-sm" style={{ fontFamily: 'Lato' }}>
                    {t('carSharing.noUsersFound')}
                  </div>
                ) : (
                  <div className="space-y-3 max-h-[60vh] overflow-y-auto">
                    {carUsers.map((u) => {
                      const isCurrentUser = u.id === user?.id;
                      const canRemove = isOriginalOwner && !isCurrentUser;
                      const canLeave = !isOriginalOwner && isCurrentUser;
                      
                      return (
                        <div
                          key={u.id}
                          className="border rounded-2xl p-4 flex items-center gap-4 bg-[#FDFDFD]"
                          style={{ borderColor: '#e4e4e7' }}
                        >
                          {/* Avatar */}
                          {u.avatarUrl ? (
                            <div className="h-12 w-12 rounded-full overflow-hidden flex-shrink-0">
                              <img 
                                src={u.avatarUrl} 
                                alt={u.displayName || u.email}
                                className="w-full h-full object-cover"
                              />
                            </div>
                          ) : (
                            <div className="h-12 w-12 rounded-full bg-[#7c9ffa] text-white flex items-center justify-center text-lg font-semibold flex-shrink-0">
                              {u.displayName?.[0]?.toUpperCase() || u.email[0]?.toUpperCase() || 'U'}
                            </div>
                          )}

                          {/* User info */}
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-gray-900 text-sm" style={{ fontFamily: 'Lato' }}>
                              {u.displayName || u.email}
                            </p>
                            <div className="flex flex-wrap gap-2 mt-1">
                              {isCurrentUser && (
                                <span className="px-1.5 py-0.5 text-[10px] border border-gray-900 rounded-full" style={{ fontFamily: 'Lato' }}>
                                  {t('carSharing.you')}
                                </span>
                              )}
                              {car?.created_by === u.id && (
                                <span className="px-1.5 py-0.5 text-[10px] border border-gray-900 rounded-full flex items-center gap-0.5" style={{ fontFamily: 'Lato' }}>
                                  <Crown className="h-2.5 w-2.5" />
                                  {t('carSharing.originalOwner')}
                                </span>
                              )}
                            </div>
                          </div>

                          {/* Action buttons */}
                          <div className="flex-shrink-0">
                            {canRemove && (
                              <Button
                                variant="destructive"
                                size="sm"
                                onClick={() => {
                                  setUserToRemove(u.id);
                                  setRemoveUserDialogOpen(true);
                                }}
                                className="text-sm"
                                style={{ fontFamily: 'Lato' }}
                              >
                                <UserMinus className="h-4 w-4 mr-1.5" />
                                {t('carSharing.removeAccess')}
                              </Button>
                            )}
                            {canLeave && (
                              <Button
                                variant="destructive"
                                size="sm"
                                onClick={() => setLeaveCarDialogOpen(true)}
                                className="text-sm"
                                style={{ fontFamily: 'Lato' }}
                              >
                                <LogOut className="h-4 w-4 mr-1.5" />
                                {t('carSharing.leaveCar')}
                              </Button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                );
              })()
            ) : selectedUserId ? (
              // Show cars for selected user
              (() => {
                const selectedUser = sharedUsers.find(u => u.id === selectedUserId);
                if (!selectedUser || selectedUser.cars.length === 0) {
                  return (
                    <div className="text-center py-8 text-gray-500 text-sm" style={{ fontFamily: 'Lato' }}>
                      {t('carSharing.noSharedCars')}
                    </div>
                  );
                }
                
                // Get cars owned by current user
                const ownedCars = sortedSharedCars.filter(car => car.created_by === user?.id);
                const ownedCarIds = new Set(ownedCars.map(c => c.id));
                
                return (
                  <>
                    <div className="space-y-2 max-h-[50vh] overflow-y-auto">
                      {selectedUser.cars.map((car) => {
                        const canRemove = ownedCarIds.has(car.id);
                        return (
                          <div 
                            key={car.id} 
                            className="flex items-center justify-between p-3 rounded-lg border border-gray-100"
                          >
                            <div 
                              className="flex-1 min-w-0 cursor-pointer"
                              onClick={() => navigate(`/cars/${car.id}`)}
                            >
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="text-sm text-gray-900 font-medium truncate" style={{ fontFamily: 'Lato' }}>
                                  {car.nickname || `${car.brand} ${car.model}`}
                                </span>
                                {car.isOwner && (
                                  <span className="px-1.5 py-0.5 text-[10px] border border-gray-900 rounded-full flex items-center gap-0.5" style={{ fontFamily: 'Lato' }}>
                                    <Crown className="h-2.5 w-2.5" />
                                    {t('carSharing.originalOwner')}
                                  </span>
                                )}
                              </div>
                              {car.year && (
                                <p className="text-xs text-gray-500 mt-1" style={{ fontFamily: 'Lato' }}>{car.year}</p>
                              )}
                            </div>
                            {canRemove ? (
                              <Button
                                variant="destructive"
                                size="sm"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleRemoveUserFromCar(car.id, selectedUserId);
                                }}
                                className="text-sm flex-shrink-0"
                                style={{ fontFamily: 'Lato' }}
                              >
                                <UserMinus className="h-4 w-4 mr-1.5" />
                                {t('carSharing.removeAccess')}
                              </Button>
                            ) : (
                              <ChevronRight className="w-5 h-5 text-gray-400 flex-shrink-0" />
                            )}
                          </div>
                        );
                      })}
                    </div>
                    {ownedCars.length > 0 && (
                      <div className="pt-4 border-t" style={{ borderColor: '#e4e4e7' }}>
                        <Button
                          variant="destructive"
                          size="sm"
                          onClick={handleRemoveUserFromAllCars}
                          className="w-full text-sm"
                          style={{ fontFamily: 'Lato' }}
                        >
                          <UserMinus className="h-4 w-4 mr-1.5" />
                          {t('carSharing.removeUserFromAllCars')}
                        </Button>
                      </div>
                    )}
                  </>
                );
              })()
            ) : null}
          </div>
        </SheetContent>
      </Sheet>

      {/* Remove User Dialog */}
      <AlertDialog open={removeUserDialogOpen} onOpenChange={setRemoveUserDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('carSharing.removeUser')}</AlertDialogTitle>
            <AlertDialogDescription>
              {carToRemoveFrom 
                ? (() => {
                    const car = sortedSharedCars.find(c => c.id === carToRemoveFrom);
                    const carName = car?.nickname || car?.brand || t('carSharing.thisCar');
                    return t('carSharing.confirmRemoveUserFromCar').replace('{carName}', carName);
                  })()
                : t('carSharing.confirmRemoveUser')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="!flex !flex-row gap-2 justify-end items-center">
            <AlertDialogCancel onClick={() => {
              setUserToRemove(null);
              setCarToRemoveFrom(null);
            }} className="!mt-0">
              {t('actions.cancel')}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleRemoveUser}
              className="bg-destructive text-white hover:bg-destructive/90"
            >
              {t('carSharing.removeUser')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Share Access Bottom Drawer */}
      <Sheet open={shareDrawerOpen} onOpenChange={(open) => {
        setShareDrawerOpen(open);
        if (!open) {
          setSelectedCarForShare(null);
          setCurrentShareLink(null);
          setLinkCopied(false);
          setActiveInvitationsForShare([]);
        }
      }}>
        <SheetContent side="bottom" className="h-auto max-h-[90vh] rounded-t-2xl p-6 border-t border-gray-200">
          <SheetHeader>
            <SheetTitle className="text-lg font-semibold text-black text-center" style={{ fontFamily: 'Lato', fontWeight: 700, fontSize: '18px', lineHeight: '24px' }}>
              {t('carSharing.inviteLink')}
            </SheetTitle>
            <p className="text-sm text-gray-500 text-center mt-1" style={{ fontFamily: 'Lato' }}>
              {selectedCarForShare 
                ? (() => {
                    const car = sortedSharedCars.find(c => c.id === selectedCarForShare);
                    const carName = car?.nickname || car?.brand || t('carSharing.thisCar');
                    return t('carSharing.temporaryLinkDescriptionWithCar').replace('{carName}', carName);
                  })()
                : t('carSharing.temporaryLinkDescriptionWithCar').replace('{carName}', t('carSharing.thisCar'))}
            </p>
          </SheetHeader>
          
          <div className="mt-6 space-y-4">
            {currentShareLink ? (
              <>
                {/* Active Link State */}
                <div className="space-y-3">
                  <div className="bg-gray-50 border border-gray-200 rounded-xl p-2.5">
                    <p className="text-sm text-gray-700 font-mono truncate" style={{ fontFamily: 'Lato' }}>
                      {currentShareLink}
                    </p>
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Clock className="h-4 w-4" style={{ color: '#7c9ffa' }} />
                      <span className="text-sm text-gray-900" style={{ fontFamily: 'Lato' }}>
                        {activeInvitationsForShare.length > 0 ? formatTimeRemaining(activeInvitationsForShare[0].expiresAt) : '48h'} {t('carSharing.or')} 1 {t('carSharing.use')} {t('carSharing.left')}
                      </span>
                    </div>
                    <button
                      onClick={handleDeactivateCurrentLink}
                      className="text-red-600 text-sm font-medium hover:text-red-700"
                      style={{ fontFamily: 'Lato' }}
                    >
                      {t('carSharing.deactivate')}
                    </button>
                  </div>
                  
                  <div className="flex gap-3 pt-2">
                    <Button
                      onClick={handleCopyLinkFromDrawer}
                      className="flex-1 h-12 rounded-[10px] bg-[#E7FE52] text-black font-medium text-sm hover:opacity-90 active:opacity-80"
                      style={{ fontFamily: 'Lato', boxShadow: '0px 1px 2px rgba(10,12.67,18,0.05)' }}
                    >
                      {linkCopied ? (
                        <>
                          <Check className="h-4 w-4 mr-2" />
                          {t('carSharing.copied')}
                        </>
                      ) : (
                        <>
                          <Copy className="h-4 w-4 mr-2" />
                          {t('carSharing.copy')}
                        </>
                      )}
                    </Button>
                    <Button
                      onClick={handleShareLink}
                      variant="secondary"
                      className="flex-1 h-12 rounded-[10px] bg-white border-2 border-gray-200 text-black font-medium text-sm hover:bg-gray-50"
                      style={{ fontFamily: 'Lato' }}
                    >
                      <Share2 className="h-4 w-4 mr-2" />
                      {t('carSharing.share')}
                    </Button>
                  </div>
                </div>
              </>
            ) : (
              <>
                {/* Inactive Link State */}
                <div className="space-y-4">
                  <div className="border-2 border-dashed border-gray-300 rounded-xl p-8 text-center">
                    <p className="text-gray-400 text-sm" style={{ fontFamily: 'Lato' }}>
                      {t('carSharing.linkInactive')}
                    </p>
                  </div>
                  
                  <div className="flex items-center justify-center gap-2">
                    <Clock className="h-4 w-4" style={{ color: '#7c9ffa' }} />
                    <span className="text-sm text-gray-600" style={{ fontFamily: 'Lato' }}>
                      48h {t('carSharing.or')} 1 {t('carSharing.use')}
                    </span>
                  </div>
                  
                  <Button
                    onClick={handleGenerateLink}
                    disabled={generatingLink}
                    className="w-full h-12 rounded-[10px] bg-[#E7FE52] text-black font-medium text-sm hover:opacity-90 active:opacity-80"
                    style={{ fontFamily: 'Lato', boxShadow: '0px 1px 2px rgba(10,12.67,18,0.05)' }}
                  >
                    {generatingLink ? t('carSharing.generating') : t('carSharing.generate')}
                  </Button>
                </div>
              </>
            )}
          </div>
        </SheetContent>
      </Sheet>

      {/* Leave Car Dialog */}
      <AlertDialog open={leaveCarDialogOpen} onOpenChange={setLeaveCarDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('carSharing.leaveCar')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('carSharing.confirmLeaveCar')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="!flex !flex-row gap-2 justify-end items-center">
            <AlertDialogCancel className="!mt-0">
              {t('actions.cancel')}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => selectedCarId && handleLeaveSharedCar(selectedCarId)}
              className="bg-destructive text-white hover:bg-destructive/90"
            >
              {t('carSharing.leaveCar')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </PageLayout>
  );
};

export default SharedAccessPage;

